import React, { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { 
 Handshake, 
 DollarSign, 
 FileText, 
 CheckCircle, 
 Clock,
 AlertTriangle,
 TrendingUp,
 Receipt,
 Building2,
 ChevronDown,
 ChevronUp,
 CreditCard
} from"lucide-react";
import { toast } from"sonner";

const SubcontractorAccountingPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [data, setData] = useState({ subcontractors: [], summary: { total_subcontractors: 0, total_contract_value: 0, total_paid: 0, total_pending: 0, total_owed: 0 } });
 const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
 const [expandedRows, setExpandedRows] = useState({});
 const [showPaymentDialog, setShowPaymentDialog] = useState(false);
 const [selectedInvoice, setSelectedInvoice] = useState(null);
 const [paymentData, setPaymentData] = useState({
 amount:"",
 payment_date: new Date().toISOString().split('T')[0],
 payment_method:"Bank Transfer",
 notes:""
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 setLoading(true);
 try {
 const response = await axios.get("/subcontractors/accounting");
 setData(response.data || { subcontractors: [], summary: {} });
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 setData({ subcontractors: [], summary: {} });
 } finally {
 setLoading(false);
 }
 };

 const openPaymentDialog = (invoice, source) => {
 setSelectedInvoice({ ...invoice, source });
 setPaymentData({
 amount: invoice.amount.toString(),
 payment_date: new Date().toISOString().split('T')[0],
 payment_method:"Bank Transfer",
 notes:""
 });
 setShowPaymentDialog(true);
 };

 const handleRecordPayment = async () => {
 if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 // For regular invoices, use the invoice payment endpoint
 if (selectedInvoice.source ==="regular") {
 await axios.post(`/invoices/${selectedInvoice.invoice_id}/payment`, {
 amount: parseFloat(paymentData.amount),
 payment_date: new Date(paymentData.payment_date).toISOString(),
 payment_method: paymentData.payment_method,
 notes: paymentData.notes || null
 });
 } else {
 // For contractor invoices, update status to paid
 await axios.patch(`/contractor-invoices/${selectedInvoice.invoice_id}/approve`, {
 action:"approve"
 });
 }
 
 toast.success(t("messages.saveSuccess"));
 setShowPaymentDialog(false);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to record payment");
 }
 };

 const toggleRow = (partnerId) => {
 setExpandedRows(prev => ({
 ...prev,
 [partnerId]: !prev[partnerId]
 }));
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case"payment_due":
 return <Badge className="bg-rose-500/10 text-rose-700 border-red-200/60">Payment Due</Badge>;
 case"invoices_pending":
 return <Badge className="bg-amber-500/10 text-amber-700 border-yellow-200/60">Invoices Pending</Badge>;
 case"up_to_date":
 return <Badge className="bg-emerald-500/10 text-emerald-700 border-green-200/60">Up to Date</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 const getInvoiceStatusBadge = (status) => {
 switch (status) {
 case"approved":
 return <Badge className="bg-emerald-500/10 text-emerald-700 border-green-200/60">Approved</Badge>;
 case"paid":
 return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200/60">Paid</Badge>;
 case"pending_engineer":
 return <Badge className="bg-amber-500/10 text-amber-700 border-yellow-200/60">Pending Engineer</Badge>;
 case"pending_accountant":
 return <Badge className="bg-blue-500/10 text-blue-700 border-blue-200/60">Pending Review</Badge>;
 case"rejected":
 return <Badge className="bg-rose-500/10 text-rose-700 border-red-200/60">Rejected</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 const { subcontractors, summary } = data;

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 Subcontractor Accounting
 </h1>
 <p className="text-slate-600 mt-1">
 Track payments and balances for all subcontractors
 </p>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-5 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600">Total Subcontractors</p>
 <p className="text-2xl font-bold text-slate-900">{summary.total_subcontractors}</p>
 </div>
 <Handshake className="w-8 h-8 text-slate-400"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Total Contract Value</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_contract_value)}</p>
 </div>
 <FileText className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Total Paid</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_paid)}</p>
 </div>
 <CheckCircle className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Pending Approval</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_pending)}</p>
 </div>
 <Clock className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Payment Due</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_owed)}</p>
 </div>
 <AlertTriangle className="h-7 w-7 text-rose-600"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Subcontractors Table */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="w-5 h-5 text-indigo-600"/>
 Subcontractor Balances
 </CardTitle>
 </CardHeader>
 <CardContent>
 {subcontractors.length === 0 ? (
 <div className="text-center py-12">
 <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600">No subcontractors found</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-slate-200 bg-white/50">
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Company</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Specialization</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Contract Value</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Total Invoiced</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Paid</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Pending</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Owed</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-[#334155]">Status</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-[#334155]">Details</th>
 </tr>
 </thead>
 <tbody>
 {subcontractors.map((sub) => (
 <React.Fragment key={sub.partner_id}>
 <tr 
 className="border-b border-white/40 hover:bg-white/80"
 >
 <td className="py-4 px-4">
 <div>
 <p className="font-medium text-slate-900">{sub.company_name}</p>
 <p className="text-[11px] text-slate-500">{sub.contact_person}</p>
 </div>
 </td>
 <td className="py-4 px-4 text-sm text-slate-600">
 {sub.specialization ||"-"}
 </td>
 <td className="py-4 px-4 text-sm font-medium text-slate-900 text-right">
 {formatCurrency(sub.total_contract_value)}
 </td>
 <td className="py-4 px-4 text-sm text-slate-900 text-right">
 {formatCurrency(sub.total_invoiced)}
 </td>
 <td className="py-4 px-4 text-sm text-slate-900 font-medium text-right">
 {formatCurrency(sub.total_paid)}
 </td>
 <td className="py-4 px-4 text-sm text-slate-900 text-right">
 {formatCurrency(sub.total_pending)}
 </td>
 <td className="py-4 px-4 text-sm font-bold text-right">
 <span className="text-slate-900">
 {formatCurrency(sub.total_owed)}
 </span>
 </td>
 <td className="py-4 px-4 text-center">
 {getStatusBadge(sub.status)}
 </td>
 <td className="py-4 px-4 text-center">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => toggleRow(sub.partner_id)}
 >
 {expandedRows[sub.partner_id] ? (
 <ChevronUp className="w-4 h-4"/>
 ) : (
 <ChevronDown className="w-4 h-4"/>
 )}
 </Button>
 </td>
 </tr>
 {/* Expanded Details */}
 {expandedRows[sub.partner_id] && (
 <tr>
 <td colSpan={9} className="bg-white/50 p-4">
 <div className="grid grid-cols-2 gap-6">
 {/* Projects */}
 <div>
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
 <Building2 className="w-4 h-4"/>
 Projects ({sub.projects?.length || 0})
 </h4>
 {sub.projects?.length > 0 ? (
 <div className="space-y-2">
 {sub.projects.map((proj, idx) => (
 <div key={idx} className="p-3 bg-white rounded border border-slate-200">
 <div className="flex justify-between items-start">
 <div>
 <p className="font-medium text-slate-900">{proj.project_name}</p>
 <p className="text-[11px] text-slate-500">{proj.service_type}</p>
 <p className="text-xs text-slate-400">{proj.contract_reference}</p>
 </div>
 <div className="text-right">
 <p className="font-semibold text-slate-900">{formatCurrency(proj.contract_value)}</p>
 <Badge variant="outline"className="text-xs">
 {proj.status}
 </Badge>
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500">No projects</p>
 )}
 </div>

 {/* Invoices */}
 <div>
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
 <Receipt className="w-4 h-4"/>
 Invoices ({sub.invoices?.length || 0})
 </h4>
 {sub.invoices?.length > 0 ? (
 <div className="space-y-2">
 {sub.invoices.map((inv, idx) => (
 <div key={idx} className="p-3 bg-white rounded border border-slate-200">
 <div className="flex justify-between items-start">
 <div>
 <p className="font-medium text-slate-900">{inv.invoice_number}</p>
 <p className="text-[11px] text-slate-500">{inv.description ||"Invoice"}</p>
 <p className="text-xs text-slate-400">
 {new Date(inv.created_at).toLocaleDateString()}
 {inv.source && <span className="ml-2 text-slate-300">({inv.source})</span>}
 </p>
 </div>
 <div className="text-right flex items-start gap-2">
 <div>
 <p className="font-semibold text-slate-900">{formatCurrency(inv.amount)}</p>
 {getInvoiceStatusBadge(inv.status)}
 </div>
 {inv.status !=="paid"&& (
 <Button
 size="sm"
 variant="outline"
 className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"
 onClick={() => openPaymentDialog(inv, inv.source)}
 data-testid={`record-payment-${inv.invoice_id}`}
 >
 <CreditCard className="w-3 h-3 mr-1"/>
 Pay
 </Button>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500">No invoices submitted</p>
 )}
 </div>
 </div>

 {/* Contact Info */}
 <div className="mt-4 pt-4 border-t border-slate-200">
 <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
 <div className="flex gap-6 text-sm">
 <div>
 <span className="text-slate-500">Email: </span>
 <span className="text-slate-900">{sub.email ||"-"}</span>
 </div>
 <div>
 <span className="text-slate-500">Phone: </span>
 <span className="text-slate-900">{sub.phone ||"-"}</span>
 </div>
 </div>
 </div>
 </td>
 </tr>
 )}
 </React.Fragment>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Payment Recording Dialog */}
 <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Record Payment</DialogTitle>
 </DialogHeader>
 {selectedInvoice && (
 <div className="py-4 space-y-4">
 <div className="p-3 bg-white/50 rounded-sm">
 <p className="text-sm text-slate-600">Invoice: {selectedInvoice.invoice_number}</p>
 <p className="font-semibold">Amount Due: {formatCurrency(selectedInvoice.amount)}</p>
 </div>
 <div>
 <Label>Payment Amount *</Label>
 <Input
 type="number"
 value={paymentData.amount}
 onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
 placeholder="Enter payment amount"
 />
 </div>
 <div>
 <Label>Payment Date</Label>
 <Input
 type="date"
 value={paymentData.payment_date}
 onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
 />
 </div>
 <div>
 <Label>Payment Method</Label>
 <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({...paymentData, payment_method: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
 <SelectItem value="Check">Check</SelectItem>
 <SelectItem value="Cash">Cash</SelectItem>
 <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Notes</Label>
 <Input
 value={paymentData.notes}
 onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
 placeholder="Payment reference or notes..."
 />
 </div>
 </div>
 )}
 <DialogFooter>
 <Button variant="outline"onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
 <Button onClick={handleRecordPayment} variant="outline"className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300">
 <CheckCircle className="w-4 h-4 mr-2"/>
 Record Payment
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default SubcontractorAccountingPage;
